import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {RouterLink} from '@angular/router';
import {AdminApiService} from '../../admin-api.service';
import {AdminTwitchBotRegistryOverview, AdminTwitchChatbotDiagnostics} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {UiButtonComponent, UiToastService} from 'freyr-ui';

@Component({
  selector: 'app-twitch-bots-page',
  standalone: true,
  imports: [CommonModule, RouterLink, UiButtonComponent, AdminTranslatePipe],
  templateUrl: './twitch-bots-page.component.html',
  styleUrl: './twitch-bots-page.component.scss'
})
export class TwitchBotsPageComponent implements OnInit {
  private static readonly pageSize = 25;

  private readonly api = inject(AdminApiService);
  private readonly i18n = inject(AdminI18nService);
  private readonly toast = inject(UiToastService);

  loading = false;
  refreshing = false;
  overview: AdminTwitchBotRegistryOverview | null = null;
  routingEntries: AdminTwitchChatbotDiagnostics[] = [];
  updatingStrictByBroadcasterId: Record<string, boolean> = {};
  currentPage = 1;

  ngOnInit(): void {
    this.loadOverview();
  }

  loadOverview(): void {
    this.loading = true;
    this.api.getTwitchBotRegistryOverview().subscribe({
      next: (overview) => {
        this.overview = overview;
        this.currentPage = 1;
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.handleError(error, 'admin.twitchBots.loadError');
      }
    });
    this.loadChatbotDiagnostics();
  }

  private loadChatbotDiagnostics(): void {
    this.api.listTwitchChatbotDiagnostics().subscribe({
      next: (entries) => {
        this.routingEntries = Array.isArray(entries) ? entries : [];
      },
      error: (error: HttpErrorResponse) => {
        this.handleError(error, 'admin.twitchBots.chatRouting.loadError');
      }
    });
  }

  refreshRegistry(): void {
    if (this.refreshing) {
      return;
    }
    this.refreshing = true;
    this.api.refreshTwitchBotRegistry().subscribe({
      next: (overview: AdminTwitchBotRegistryOverview) => {
        this.refreshing = false;
        this.overview = overview;
        this.currentPage = 1;
        const success = (overview.lastRefreshStatus || 'IDLE').toUpperCase() === 'SUCCESS';
        const message = success
          ? this.i18n.t('admin.twitchBots.refreshSuccess')
          : (overview.lastRefreshError?.trim() || this.i18n.t('admin.twitchBots.refreshError'));
        if (success) {
          this.toast.success(message);
        } else {
          this.toast.error(message);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.refreshing = false;
        this.handleError(error, 'admin.twitchBots.refreshError');
      }
    });
  }

  get pagedEntries() {
    const entries = this.overview?.entries ?? [];
    const start = (this.currentPage - 1) * TwitchBotsPageComponent.pageSize;
    return entries.slice(start, start + TwitchBotsPageComponent.pageSize);
  }

  get totalPages(): number {
    const totalEntries = this.overview?.entries?.length ?? 0;
    return Math.max(1, Math.ceil(totalEntries / TwitchBotsPageComponent.pageSize));
  }

  get paginationStart(): number {
    if (!this.overview?.entries?.length) {
      return 0;
    }
    return (this.currentPage - 1) * TwitchBotsPageComponent.pageSize + 1;
  }

  get paginationEnd(): number {
    const totalEntries = this.overview?.entries?.length ?? 0;
    return Math.min(this.currentPage * TwitchBotsPageComponent.pageSize, totalEntries);
  }

  previousPage(): void {
    this.currentPage = Math.max(1, this.currentPage - 1);
  }

  nextPage(): void {
    this.currentPage = Math.min(this.totalPages, this.currentPage + 1);
  }

  statusLabelKey(status?: string | null): string {
    return `admin.twitchBots.status.${(status || 'IDLE').toUpperCase()}`;
  }

  statusBadgeClass(status?: string | null): string {
    switch ((status || 'IDLE').toUpperCase()) {
      case 'SUCCESS':
        return 'status-badge status-badge--success';
      case 'ERROR':
        return 'status-badge status-badge--error';
      default:
        return 'status-badge status-badge--idle';
    }
  }

  strictModeBadgeClass(enabled: boolean): string {
    return enabled ? 'status-badge status-badge--success' : 'status-badge status-badge--idle';
  }

  canEnableStrictMode(entry: AdminTwitchChatbotDiagnostics): boolean {
    return !!entry.chatCommandsViaEventsub;
  }

  chatbotLevel(entry: AdminTwitchChatbotDiagnostics): 'ready' | 'degraded' | 'blocked' {
    switch ((entry.complianceState || 'UNKNOWN').toUpperCase()) {
      case 'CHATBOT_READY':
        return 'ready';
      case 'EVENTSUB_OK_IRC_FALLBACK_ENABLED':
      case 'EVENTSUB_OK_USER_TOKEN_FALLBACK':
        return 'degraded';
      default:
        return 'blocked';
    }
  }

  chatbotLevelBadgeClass(entry: AdminTwitchChatbotDiagnostics): string {
    switch (this.chatbotLevel(entry)) {
      case 'ready':
        return 'status-badge status-badge--success';
      case 'degraded':
        return 'status-badge status-badge--warning';
      default:
        return 'status-badge status-badge--error';
    }
  }

  chatbotLevelLabelKey(entry: AdminTwitchChatbotDiagnostics): string {
    return `admin.twitchBots.chatRouting.level.${this.chatbotLevel(entry)}`;
  }

  complianceStatusKey(entry: AdminTwitchChatbotDiagnostics): string {
    return `admin.twitchBots.chatRouting.status.${(entry.complianceState || 'UNKNOWN').toUpperCase()}`;
  }

  complianceDescriptionKey(entry: AdminTwitchChatbotDiagnostics): string {
    return `admin.twitchBots.chatRouting.description.${(entry.complianceState || 'UNKNOWN').toUpperCase()}`;
  }

  deliveryModeKey(entry: AdminTwitchChatbotDiagnostics): string {
    return `admin.twitchBots.chatRouting.deliveryMode.${(entry.deliveryTokenMode || 'MISSING').toUpperCase()}`;
  }

  nextStepKey(entry: AdminTwitchChatbotDiagnostics): string {
    const state = (entry.complianceState || 'UNKNOWN').toUpperCase();
    switch (state) {
      case 'CHATBOT_READY':
        return 'admin.twitchBots.chatRouting.nextStep.NONE';
      case 'EVENTSUB_OK_IRC_FALLBACK_ENABLED':
        return entry.chatbotStrictHelixOnly
          ? 'admin.twitchBots.chatRouting.nextStep.INSPECT_RUNTIME'
          : 'admin.twitchBots.chatRouting.nextStep.ENABLE_STRICT';
      case 'EVENTSUB_OK_USER_TOKEN_FALLBACK':
        return entry.chatbotStrictHelixOnly
          ? 'admin.twitchBots.chatRouting.nextStep.CONFIGURE_APP_TOKEN'
          : 'admin.twitchBots.chatRouting.nextStep.ENABLE_STRICT';
      case 'EVENTSUB_OK_APP_TOKEN_MISSING':
        return 'admin.twitchBots.chatRouting.nextStep.CONFIGURE_APP_TOKEN';
      case 'APP_TOKEN_OK_SENDER_ID_MISSING':
        return 'admin.twitchBots.chatRouting.nextStep.SET_BOT_USER_ID';
      case 'BOT_IS_BROADCASTER':
        return 'admin.twitchBots.chatRouting.nextStep.VERIFY_DEDICATED_BOT';
      case 'IRC_ONLY':
        return 'admin.twitchBots.chatRouting.nextStep.ENABLE_EVENTSUB';
      case 'CHANNEL_MISSING':
        return 'admin.twitchBots.chatRouting.nextStep.RELINK_TWITCH';
      default:
        return 'admin.twitchBots.chatRouting.nextStep.INSPECT_RUNTIME';
    }
  }

  updateStrictMode(entry: AdminTwitchChatbotDiagnostics, chatbotStrictHelixOnly: boolean): void {
    if (this.updatingStrictByBroadcasterId[entry.broadcasterId]) {
      return;
    }
    if (chatbotStrictHelixOnly && !this.canEnableStrictMode(entry)) {
      this.toast.error(this.i18n.t('admin.twitchBots.chatRouting.enableBlocked'));
      return;
    }
    this.updatingStrictByBroadcasterId = {
      ...this.updatingStrictByBroadcasterId,
      [entry.broadcasterId]: true
    };
    this.api.updateTwitchChatbotStrictMode(entry.broadcasterId, chatbotStrictHelixOnly).subscribe({
      next: () => {
        this.updatingStrictByBroadcasterId = {
          ...this.updatingStrictByBroadcasterId,
          [entry.broadcasterId]: false
        };
        this.loadChatbotDiagnostics();
        this.toast.success(this.i18n.t(
          chatbotStrictHelixOnly
            ? 'admin.twitchBots.chatRouting.strictEnabled'
            : 'admin.twitchBots.chatRouting.strictDisabled',
          { broadcasterId: entry.broadcasterId }
        ));
      },
      error: (error: HttpErrorResponse) => {
        this.updatingStrictByBroadcasterId = {
          ...this.updatingStrictByBroadcasterId,
          [entry.broadcasterId]: false
        };
        this.handleError(error, 'admin.twitchBots.chatRouting.updateError');
      }
    });
  }

  private handleError(error: HttpErrorResponse, fallbackKey: string): void {
    const message = typeof error.error?.message === 'string' && error.error.message.trim()
      ? error.error.message.trim()
      : this.i18n.t(fallbackKey);
    this.toast.error(message);
  }
}
